import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Chip,
  Typography,
  useTheme,
  IconButton,
  DialogTitle,
  Avatar,
  TextField,
  Stepper,
  Step,
  StepLabel,
  Menu,
  MenuItem,
  ListItemIcon,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import {
  CloseRounded,
  ArrowDropDownRounded,
  LaunchRounded,
  ModeEditRounded,
  RadioButtonUncheckedRounded,
  CheckCircleRounded,
} from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { updateFeedbackStatusSuccess } from "../../../core/events/sessionEvents";
import sessionService from "../../../core/api/sessionService";
import { useSnackbar } from "notistack";
import HighlightText from "../../AI_Search/AI_Search/components/Feedback/HighlightText";
import { AI_SEARCH_URL } from "../../../utils/routes";
import { formatDateWithTime } from "./utils";
import { getUserDetails } from "../../../core/storage/localStorage";
import { getUserNameShortform } from "../../../utils/utils";
import { deleteFeedback, updateFeedback } from "../../../core/repo/sessionRepo";
import { actionLabels } from "./ViewFeedback";
import FeedbackChips, { reasonGroups } from "../../../components/FeedbackChips";
import { usePostHog } from "posthog-js/react";
import {
  APPROVE_FEEDBACK_SUCCESS,
  APPROVE_FEEDBACK_FAILURE,
  REJECT_FEEDBACK_SUCCESS,
  REJECT_FEEDBACK_FAILURE,
  GO_TO_ANSWER,
} from "../../../utils/posthogEvents";
import { useThemeSettings } from "../../../theme/ThemeSettingsProvider";

const useStyles = makeStyles()((theme) => ({
  dialogContent: {
    padding: theme.spacing(2),
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
  },
  feedbackHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: theme.spacing(2),
    position: "relative",
  },
  feedbackContent: {
    marginTop: theme.spacing(2),
    flex: 1,
    display: "flex",
    flexDirection: "column",
  },
  feedbackChip: {
    margin: theme.spacing(0.5),
  },
  highlightedSection: {
    backgroundColor: theme.palette.primary.lighter,
    padding: theme.spacing(1),
    borderRadius: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  questionContent: {
    fontWeight: 500,
    marginBottom: theme.spacing(1),
  },
  answerContent: {
    color: theme.palette.text.secondary,
  },
  dateText: {
    color: theme.palette.text.secondary,
    fontSize: "0.75rem",
    marginTop: theme.spacing(0.5),
    marginLeft: theme.spacing(4),
  },
  ratingIcon: {
    marginRight: theme.spacing(1),
  },
  partialText: {
    color: theme.palette.error.main,
    marginLeft: theme.spacing(4),
    marginTop: theme.spacing(0.5),
  },
  highlightedText: {
    backgroundColor: theme.palette.warning.main,
    padding: "2px 4px",
    borderRadius: "2px",
    color: theme.palette.common.black,
  },
  closeButton: {
    position: "absolute",
    right: 0,
    top: 0,
  },
  statusChip: {
    marginLeft: theme.spacing(1),
  },
  sourcesContainer: {
    marginTop: theme.spacing(2),
  },
  citationCard: {
    border: `1px solid ${theme.palette.divider}`,
    marginRight: 16,
    marginBottom: 4,
    borderRadius: 12,
    minWidth: 300,
    maxWidth: 300,
  },
  text: {
    whiteSpace: "pre-wrap",
    wordWrap: "break-word",
    overflowWrap: "break-word",
    wordBreak: "break-word",
    display: "-webkit-box",
    overflow: "hidden",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: 2,
  },
  aiModelFixChip: {
    backgroundColor: "#F9FBE7",
    color: "#827717",
  },
  docUpdateChip: {
    backgroundColor: "#E3F2FD",
    color: "#0D47A1",
  },
  gradientText: {
    background: theme.palette.accentGradient,
    backgroundClip: "text",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
}));

const steps = ["Feedback Raised", "Notified", "Resolved"];

const CustomStepIcon = (props) => {
  const { active, completed } = props;
  const theme = useTheme();

  if (completed) {
    return (
      <CheckCircleRounded
        sx={{ fontSize: 16, color: theme.palette.success.main }}
      />
    );
  }
  return (
    <RadioButtonUncheckedRounded
      sx={{ fontSize: 16, color: theme.palette.action.disabled }}
    />
  );
};

function ViewFeedbackDetail({ feedback, onClose }) {
  const { mode } = useThemeSettings();
  const isDarkMode = mode === "dark";
  const posthog = usePostHog();
  const { classes } = useStyles();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const editorRef = useRef();
  const user = getUserDetails();
  const [isEditing, setIsEditing] = useState(false);
  const [currentFeedback, setCurrentFeedback] = useState(feedback);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  if (!feedback) return null;

  const isPositive =
    feedback.rating === "positive" || feedback.rating === "good";

  const handleUpdateStatus = async (status) => {
    if (!feedback || !feedback.message_id) {
      enqueueSnackbar("Cannot update feedback: missing message ID", {
        variant: "error",
      });
      return;
    }

    try {
      await sessionService.updateFeedbackStatus(feedback.message_id, status);
      dispatch(updateFeedbackStatusSuccess(feedback.message_id, status));
      enqueueSnackbar(`Feedback ${status} successfully`, {
        variant: "success",
      });
      posthog.capture(APPROVE_FEEDBACK_SUCCESS, {
        feedback_message_id: feedback.message_id,
      });
      onClose();
    } catch (error) {
      console.error("Error updating feedback status:", error);
      enqueueSnackbar(`Failed to ${status} feedback`, { variant: "error" });
      posthog.capture(APPROVE_FEEDBACK_FAILURE, {
        feedback_message_id: feedback.message_id,
      });
    }
  };

  const handleRejectFeedback = async () => {
    try {
      await dispatch(deleteFeedback(feedback.message_id));
      enqueueSnackbar("Feedback rejected successfully", { variant: "success" });
      posthog.capture(REJECT_FEEDBACK_SUCCESS, {
        feedback_message_id: feedback.message_id,
      });
      onClose();
    } catch (error) {
      console.error("Error rejecting feedback:", error);
      enqueueSnackbar("Failed to reject feedback", { variant: "error" });
      posthog.capture(REJECT_FEEDBACK_FAILURE, {
        feedback_message_id: feedback.message_id,
      });
    }
  };

  const handleSaveFeedback = async () => {
    try {
      await dispatch(updateFeedback(feedback.message_id, currentFeedback));
      enqueueSnackbar("Feedback saved successfully", { variant: "success" });
      setIsEditing(false);
    } catch (error) {
      console.error("Error saving feedback:", error);
      enqueueSnackbar("Failed to save feedback", { variant: "error" });
    }
  };

  const handleActionChange = async (action) => {
    try {
      const updatedFeedback = {
        ...currentFeedback,
        action,
      };
      setCurrentFeedback(updatedFeedback);
      await dispatch(updateFeedback(feedback.message_id, updatedFeedback));
    } catch (error) {
      console.error("Error updating feedback:", error);
    }
    setAnchorEl(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "approved":
        return "success";
      case "rejected":
        return "error";
      default:
        return "default";
    }
  };

  const handleOpenMessage = () => {
    if (feedback.message_id && feedback.conversation_id) {
      let url = `${AI_SEARCH_URL}/${feedback.conversation_id}`;
      const searchParams = new URLSearchParams({
        messageId: feedback.message_id,
      }).toString();
      const fullUrl = `${url}?${searchParams}`;
      window.open(fullUrl, "_blank");
      posthog.capture(GO_TO_ANSWER);
    }
  };

  const handleOpenActionMenu = (e) => {
    if (feedback.approval_status) return;
    setAnchorEl(e.currentTarget);
  };

  const chips = isPositive ? [feedback?.rating] : feedback?.feedback.split(",");
  const stepperStep = !feedback.approval_status
    ? 1
    : feedback.approval_status === "notified"
    ? 2
    : 3;

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          height: "90vh",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
        },
      }}>
      <DialogTitle
        component={Box}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{ pb: 0, mb: 0 }}>
        <Typography fontWeight={500}>
          #{feedback.id}{" "}
          {feedback.action === "ai_model_fix"
            ? "Feedback for Raven"
            : "Document Feedback"}
        </Typography>
        <Box display="flex" alignItems="center" gap={2}>
          <Stepper
            activeStep={stepperStep}
            alternativeLabel
            sx={{
              "& .MuiStepConnector-root": {
                top: 6,
                left: "calc(-50% + 6.5px)",
                right: "calc(50% + 6.5px)",
              },
              "& .MuiStepConnector-line": {
                borderColor: theme.palette.action.disabled,
                borderTopWidth: 4,
                borderRadius: 0,
              },
              "& .Mui-completed .MuiStepConnector-line": {
                borderColor: theme.palette.success.main,
              },
            }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel
                  StepIconComponent={CustomStepIcon}
                  sx={{
                    minWidth: 100,
                    "& .MuiStepLabel-label": {
                      marginTop: 1,
                      color: theme.palette.text.secondary,
                      "&.Mui-active": {
                        color: theme.palette.text.primary,
                        fontWeight: 500,
                      },
                      "&.Mui-completed": {
                        color: theme.palette.text.primary,
                      },
                    },
                  }}>
                  <Typography variant="caption">{label}</Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
          <IconButton onClick={onClose}>
            <CloseRounded />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers className={classes.dialogContent}>
        {/* Question Section */}
        <Box mb={4}>
          <Typography variant="h6" fontWeight={500}>
            {feedback.question_content || "No question content"}
          </Typography>
          {/* Date below question */}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 0.5 }}>
            {formatDateWithTime(feedback.created_at)}
          </Typography>
        </Box>
        {/* Answer & Feedback Side by Side */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 3,
            width: "100%",
          }}
          mb={4}>
          {/* Answer Section */}
          <Box
            sx={{
              width: { xs: "100%", md: "50%" },
              display: "flex",
              flexDirection: "column",
            }}>
            {/* Answer Header - Outside the box */}
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={1}>
              <Typography fontWeight={500}>Answer</Typography>
              <Button
                variant="text"
                color="primary"
                startIcon={<LaunchRounded />}
                onClick={handleOpenMessage}
                sx={{ fontSize: 13 }}>
                Go to Answer
              </Button>
            </Box>
            <Box
              sx={{
                bgcolor: (theme) => theme.palette.background.light,
                borderRadius: 3,
                p: 2,
                border: (theme) => `1px solid ${theme.palette.divider}`,
                display: "flex",
                flexDirection: "column",
                minHeight: 200,
                flex: 1,
              }}>
              <Box
                sx={{
                  fontSize: (theme) => theme.typography.caption.fontSize,
                  lineHeight: (theme) => theme.typography.caption.lineHeight,
                  fontFamily: (theme) => theme.typography.caption.fontFamily,
                  fontWeight: (theme) => theme.typography.caption.fontWeight,
                  letterSpacing: (theme) =>
                    theme.typography.caption.letterSpacing,
                  color: (theme) => theme.palette.text.secondary,
                  background: (theme) => theme.palette.background.light,
                  borderRadius: 2,
                  p: 1.5,
                  minHeight: 120,
                  flex: 1,
                }}>
                <HighlightText
                  editorRef={editorRef}
                  message={feedback.answer_content}
                  typeOfAnswer={"complete_answer"}
                  highlightedSections={feedback.highlighted_sections}
                />
              </Box>
            </Box>
          </Box>
          {/* Feedback Section */}
          <Box
            sx={{
              width: { xs: "100%", md: "50%" },
              display: "flex",
              flexDirection: "column",
            }}>
            {/* Feedback Header - Outside the box */}
            <Box mb={1} display="flex" alignItems="center" gap={1.2}>
              <Typography fontWeight={500}>Feedback by</Typography>
              <Avatar
                sx={{
                  width: 22,
                  height: 22,
                  fontSize: 14,
                  background: "#455A64",
                }}>
                {getUserNameShortform(user?.name)}
              </Avatar>
              <Typography variant="body2">{user?.name}</Typography>
            </Box>
            <Box
              sx={{
                bgcolor: theme.palette.background.default,
                borderRadius: 3,
                p: 2,
                border: (theme) => `1px solid ${theme.palette.divider}`,
                display: "flex",
                flexDirection: "column",
                minHeight: 200,
                flex: 1,
              }}>
              <FeedbackChips chips={chips} readonly />
              {/* Comment section styled like InputBase but readonly */}
              <Typography variant="caption" fontWeight={600} sx={{ mt: 1 }}>
                Additional Feedback
              </Typography>
              <Box
                sx={{
                  flex: 1,
                  minHeight: "100px",
                  overflow: "auto",
                  alignItems: "start",
                  padding: 2,
                  borderRadius: 2,
                  border: isDarkMode
                    ? `1px solid ${theme.palette.primary.main}`
                    : "1px solid rgba(0, 0, 0, 0.23)",
                  background: isDarkMode
                    ? theme.palette.background.light
                    : "#f9f9f9",
                  fontSize: 14,
                  marginTop: 1,
                  color: "inherit",
                  whiteSpace: "pre-wrap",
                }}>
                {currentFeedback.description || (
                  <span style={{ color: "#aaa" }}>No additional feedback</span>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Box
          width="100%"
          display="flex"
          justifyContent="space-between"
          alignItems="center">
          {/* Left: Reject button */}
          {!feedback.approval_status ? (
            <Button
              onClick={handleRejectFeedback}
              color="primary"
              variant="outlined">
              <span className={classes.gradientText}>Reject</span>
            </Button>
          ) : (
            <span />
          )}
          {/* Right: Close and Approve/Mark as Resolved buttons */}
          <Box display="flex" gap={1}>
            <Button onClick={onClose} color="primary" variant="text">
              Close
            </Button>
            {!feedback.approval_status && (
              <Button
                variant="contained"
                onClick={() =>
                  handleUpdateStatus(
                    currentFeedback.action === "ai_model_fix"
                      ? "notified"
                      : "resolved"
                  )
                }
                color="primary">
                {currentFeedback.action === "ai_model_fix"
                  ? "Approve"
                  : "Mark as Resolved"}
              </Button>
            )}
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default ViewFeedbackDetail;
